'use client';

import { useContext } from 'react';
import { useCmsHashState } from '../../hooks/useHashState';
import { VerticalType } from '../../types/cmsTypes';
import { CmsClientContext } from '../../utils/cmsPageContext/CmsClientContext';
import { isVerticalType } from '../../utils/isVerticalType';

export type ProductBlockAsideModalState = {
  modalProductUrlAlias?: string;
  modalProductIdScrollTo?: string;
  modalProductVertical?: string;
};

// Hook is in its own module so consumers that only open/close the modal
// (no rendering) don't pull the modal component's chunk into their bundle.
export const useProductBlockAsideModal = () => {
  const [params, setHashParams] = useCmsHashState<ProductBlockAsideModalState>({
    modalProductUrlAlias: 'string',
    modalProductIdScrollTo: 'string',
    modalProductVertical: 'string',
  });
  // The opener's vertical travels with the hash state: a product block scoped to
  // its own vertical opens the page-wide drawer under that vertical (CMS-1987).
  const openerVertical = useContext(CmsClientContext)?.vertical;

  const modalProductUrlAlias = params.modalProductUrlAlias ?? '';
  const modalProductIdScrollTo = params.modalProductIdScrollTo ?? '';
  // Hash values are user-editable: an unknown vertical falls back to the page's.
  const modalProductVertical: VerticalType | undefined = isVerticalType(
    params.modalProductVertical,
  )
    ? params.modalProductVertical
    : undefined;

  const openModal = (
    modalProductUrlAlias: string,
    modalProductIdScrollTo: string | null,
  ) => {
    setHashParams({
      modalProductUrlAlias,
      modalProductIdScrollTo,
      modalProductVertical: openerVertical,
    });

    return true;
  };

  const closeModal = () => {
    setHashParams({
      modalProductUrlAlias: undefined,
      modalProductIdScrollTo: undefined,
      modalProductVertical: undefined,
    });

    return false;
  };

  return {
    openModal,
    closeModal,
    modalProductUrlAlias,
    modalProductIdScrollTo,
    modalProductVertical,
  };
};
